import './styles/graphic.scss';
import mapboxgl from 'mapbox-gl';
import dayjs from 'dayjs';
import * as d3 from 'd3';
import { nwsColors } from './data/nws-colors';
import meta from './data/meta.json';


// Calculate centroid of a polygon/multipolygon geometry
const getCentroid = (geometry) => {
  const coords = geometry.type === 'MultiPolygon'
    ? geometry.coordinates[0][0]
    : geometry.coordinates[0];

  let x = 0; let y = 0; let
    area = 0;
  for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    const xi = coords[i][0]; const
      yi = coords[i][1];
    const xj = coords[j][0]; const
      yj = coords[j][1];
    const f = xi * yj - xj * yi;
    x += (xi + xj) * f;
    y += (yi + yj) * f;
    area += f;
  }
  area *= 3;
  return area === 0 ? coords[0] : [x / area, y / area];
};

// Convert county polygons to centroid points
const toPointFeatures = featureCollection => ({
  type: 'FeatureCollection',
  features: featureCollection.features
    .filter(f => f.geometry && f.properties.out != null && f.properties.out > 0)
    .map(f => ({
      type: 'Feature',
      properties: f.properties,
      geometry: {
        type: 'Point',
        coordinates: getCentroid(f.geometry),
      },
    })),
});

// Calculate the max "out" value from the data
const getMaxOutValue = (featureCollection) => {
  const values = featureCollection.features
    .map(f => f.properties.out)
    .filter(v => v != null && v > 0);
  return Math.max(...values);
};

const drawOutagesLegend = () => {
  const legendCircles = document.querySelector('.legend-circles');
  if (!legendCircles) return;

  legendCircles.innerHTML = '';

  // Fixed scale: 150k max value, 30px max radius (matching map layer)
  const maxValue = 150000;
  const maxRadius = 30;
  const legendValues = [10000, 50000, 140000];

  legendValues.forEach((value) => {
    const radius = (Math.sqrt(value) / Math.sqrt(maxValue)) * maxRadius;

    const item = document.createElement('div');
    item.className = 'legend-circle-item';

    const circle = document.createElement('div');
    circle.className = 'legend-circle';
    const size = radius * 2;
    circle.style.width = `${size}px`;
    circle.style.height = `${size}px`;

    const label = document.createElement('span');
    label.className = 'legend-circle-label';
    label.textContent = value >= 1000 ? `${(value / 1000).toLocaleString()}k` : value.toLocaleString();

    item.appendChild(circle);
    item.appendChild(label);
    legendCircles.appendChild(item);
  });
};

const drawLegend = () => {
  const legendGradient = document.querySelector('.legend-gradient');
  const legendTicks = document.querySelector('.legend-ticks');
  const legendLabels = document.querySelector('.legend-labels');
  if (!legendGradient || !legendTicks || !legendLabels) return;

  legendGradient.innerHTML = '';
  legendTicks.innerHTML = '';
  legendLabels.innerHTML = '';

  // Determine seasonal temperature range based on current month
  const currentMonth = new Date().getMonth(); // 0 = January, 11 = December
  let minTemp;
  let maxTemp;
  let tickValues;

  if (currentMonth === 11 || currentMonth === 0 || currentMonth === 1) {
    // Winter: December, January, February
    minTemp = -20;
    maxTemp = 80;
    tickValues = [-20, 0, 20, 40, 60, 80];
  } else if (currentMonth >= 2 && currentMonth <= 4) {
    // Spring: March, April, May
    minTemp = 10;
    maxTemp = 100;
    tickValues = [10, 30, 50, 70, 90];
  } else if (currentMonth >= 5 && currentMonth <= 7) {
    // Summer: June, July, August
    minTemp = 40;
    maxTemp = 125;
    tickValues = [40, 60, 80, 100, 120];
  } else {
    // Fall: September, October, November
    minTemp = 10;
    maxTemp = 100;
    tickValues = [10, 30, 50, 70, 90];
  }

  const tempColors = nwsColors.filter(c => c[0] >= minTemp && c[0] <= maxTemp);

  tempColors.forEach((color) => {
    const block = document.createElement('div');
    block.className = 'legend-color-block';
    block.style.backgroundColor = `rgb(${color[1]}, ${color[2]}, ${color[3]})`;
    legendGradient.appendChild(block);
  });

  // Add tick marks and labels for specific temperature values
  tickValues.forEach((temp) => {
    const position = ((temp - minTemp) / (maxTemp - minTemp)) * 100;

    // Create tick mark
    const tick = document.createElement('div');
    tick.className = 'legend-tick';
    tick.style.left = `${position}%`;
    legendTicks.appendChild(tick);

    // Create label
    const label = document.createElement('li');
    label.textContent = temp;
    label.style.left = `${position}%`;
    legendLabels.appendChild(label);
  });
};

const drawMap = async (container, isMobile, mapParams = {}) => {
  // Fetches JSON data from a given URL
  const fetchData = async (url) => {
    try {
      const response = await fetch(url);
      const data = await response.json();
      return data;
    } catch (error) {
      // Logs an error if fetching data fails
      console.error('Error fetching data:', error);
      return null; // Return null on error
    }
  };

  const url = 'https://ix.cnn.io/data/apparent-temperature/city_forecasts.json';
  const forecastData = await fetchData(url);

  if (!forecastData || !forecastData.forecasts || forecastData.forecasts.length === 0) {
    console.error('Failed to load or parse forecast data. Not drawing map.');
    // The pym child may not be available, so we'll directly select the container if needed.
    const mapContainer = document.getElementById('map-content-wrapper');
    if (mapContainer) {
      mapContainer.innerHTML = '<p style="text-align: center; padding: 20px;">Could not load temperature forecast data at this time.</p>';
    }
    return;
  }


  // Set dek content if it's empty (fixes staging environment issue)
  const subhedDekElement = document.querySelector('.subhed.dek');
  if (subhedDekElement && (!subhedDekElement.textContent || subhedDekElement.textContent.trim() === '')) {
    subhedDekElement.textContent = meta.subhed;
  }

  drawLegend();

  mapboxgl.accessToken = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJiYjIwMTgxNDZmNjgyODllMjAxYTYwOTAxYmY2MzI2MyJ9.8F6TQITXymFzCXy8tBqGgg';

  const optionsSm = {
    initZoom: 3.5,
    minZoom: 2,
    maxZoom: 7,
    center: [-87.48870713823258, 33.97824318321759],
    maxBounds: [[-126, 20], [-65, 55]],
  };

  const optionsLg = {
    initZoom: 4.5,
    minZoom: 3.25,
    maxZoom: 6,
    center: [-87.48870713823258, 33.97824318321759],
    maxBounds: [[-126, 20], [-65, 55]],
  };

  const options = isMobile ? optionsSm : optionsLg;

  // Override with query params if provided
  const mapCenter = mapParams.center || options.center;
  const mapZoom = mapParams.zoom || options.initZoom;

  const map = new mapboxgl.Map({
    container: container.node(),
    style: 'mapbox://styles/cnndigital/cmbfliit7009r01s1hvcoe2pb',
    center: mapCenter,
    zoom: mapZoom,
    maxZoom: options.maxZoom,
    minZoom: options.minZoom,
    maxBounds: options.maxBounds,
  });

  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();
  // map.dragPan.disable();

  if (!isMobile) {
    const nav = new mapboxgl.NavigationControl({
      showCompass: false,
    });
    map.addControl(nav, 'bottom-right');
  }

  let currentLayerId = null;
  let currentSourceId = null;

  const showErrorMessage = () => {
    const mapContainer = document.getElementById('map');
    if (!mapContainer) return;

    // Create overlay div
    const overlay = document.createElement('div');
    overlay.id = 'map-error-overlay';
    overlay.innerHTML = `
      <div class="error-message">
        <h3>Still processing latest forecast</h3>
        <p>Try again later.</p>
      </div>
    `;

    // Style the overlay
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(240, 240, 240, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      font-family: "CNN", "CNN Sans Display", "CNN Sans", "Helvetica Neue", Helvetica, Utkal, Arial, sans-serif;
    `;

    overlay.querySelector('.error-message').style.cssText = `
      text-align: center;
      background: white;
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    `;

    overlay.querySelector('h3').style.cssText = `
      margin: 0 0 10px 0;
      color: #262626;
      font-size: 18px;
      font-weight: 700;
    `;

    overlay.querySelector('p').style.cssText = `
      margin: 0;
      color: #595959;
      font-size: 14px;
    `;

    mapContainer.appendChild(overlay);
  };

  const hideErrorMessage = () => {
    const overlay = document.getElementById('map-error-overlay');
    if (overlay) {
      overlay.remove();
    }
  };

  const updateMapLayer = (forecast, beforeLayerId) => {
    if (!map) return;
    if (currentLayerId && map.getLayer(currentLayerId)) {
      map.removeLayer(currentLayerId);
    }
    if (currentSourceId && map.getSource(currentSourceId)) {
      map.removeSource(currentSourceId);
    }

    const dayId = forecast.day; // e.g., 'day_1'
    currentSourceId = `apparent-temp-source-${dayId}`;
    currentLayerId = `apparent-temp-layer-${dayId}`;
    const tilesetId = forecast.mapbox_tileset_id; // Use the tileset ID directly from the forecast object

    map.addSource(currentSourceId, {
      type: 'raster',
      url: `mapbox://${tilesetId}`,
      tileSize: 512,
    });

    map.addLayer({
      id: currentLayerId,
      type: 'raster',
      source: currentSourceId,
      paint: {
        'raster-opacity': 1,
      },
    }, beforeLayerId);

    // Hide error message when layer loads successfully
    hideErrorMessage();
  };

  map.on('load', () => {
    let firstSymbolId;
    for (const layer of map.getStyle().layers) {
      if (layer.type === 'symbol') {
        firstSymbolId = layer.id;
        break;
      }
    }

    map.addSource('us-states', {
      type: 'geojson',
      data: 'https://ix.cnn.io/data/gis/usa_states_conus.geojson',
    });

    const borderLayerId = 'state-borders-override';
    map.addLayer({
      id: borderLayerId,
      type: 'line',
      source: 'us-states',
      paint: {
        'line-color': '#ffffff',
        'line-width': 0.5,
      },
    }, firstSymbolId);


    const outagesURL = 'https://ix.cnn.io/data/power-outages/latest-mercator.geojson';
    const outagesMetaURL = 'https://ix.cnn.io/data/power-outages/latest.json';
    // Load and add county outages layer

    Promise.all([d3.json(outagesURL), d3.json(outagesMetaURL)]).then(([outagesData, outagesMeta]) => {
      if (!outagesData || !outagesData.features) {
        console.error('Failed to load outages data');
        return;
      }


      const pointData = toPointFeatures(outagesData);

      // Draw legend (uses fixed 150k max)
      drawOutagesLegend();

      // Fixed scale max for circle sizing
      const scaleMax = 150000;

      map.addSource('county-outages', {
        type: 'geojson',
        data: pointData,
      });

      map.addLayer({
        id: 'county-outages-circles',
        type: 'circle',
        source: 'county-outages',
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['sqrt', ['get', 'out']], // Scale by sqrt for area-proportional sizing
            0, 0,
            Math.sqrt(scaleMax), 30, // sqrt(150k) -> 30px max radius
          ],
          'circle-color': '#333333',
          'circle-opacity': 0.8,
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1,
        },
      }, firstSymbolId); // Add below labels

      // Tooltip for outages circles
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      });

      map.on('click', 'county-outages-circles', (e) => {
        const feature = e.features[0];
        const props = feature.properties;
        const coords = feature.geometry.coordinates.slice();

        // Format the tooltip content
        const countyName = props.NAMELSAD || 'Unknown';
        const stateName = props.STUSPS || '';
        const customersOut = props.out != null ? props.out.toLocaleString() : 'N/A';

        let html = `<strong>${countyName}${stateName ? `, ${stateName}` : ''}</strong>`;
        html += `<br>${customersOut} without power`;

        popup.setLngLat(coords).setHTML(html).addTo(map);
      });

      // Close popup when clicking elsewhere on the map
      map.on('click', (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['county-outages-circles'] });
        if (!features.length) {
          popup.remove();
        }
      });

      // Cursor change on hover
      map.on('mouseenter', 'county-outages-circles', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', 'county-outages-circles', () => {
        map.getCanvas().style.cursor = '';
      });
      // Sets the timestamp using the summary data
      if (forecastData.last_updated_str && outagesMeta.as_of) {
        const outageDate = new Date(outagesMeta.as_of);
        const timestampString = `Notes: Temperature data forecast for 7 a.m. ET on each day. Temperature data last checked on ${forecastData.last_updated_str}. Values reflect the forecasted "apparent temperature," which is the perceived temperature derived from a combination of temperature and wind. Utility data as of ${dayjs(outageDate).format('MMMM D, YYYY h:mm A')} and based on utilities' self-reported outage information on their public websites, by county.`;
        const timestampEl = document.getElementById('timestamp');
        if (timestampEl) {
          timestampEl.innerHTML = timestampString;
        }
      }
    });

    map.on('move', () => {
      const zoom = map.getZoom();
      const center = map.getCenter();
      console.log(zoom, center);
    });

    const rasterBeforeLayerId = borderLayerId;

    const controlsContainer = document.getElementById('forecast-day-controls');
    if (controlsContainer) {
      controlsContainer.innerHTML = ''; // Clear previous controls
      forecastData.availableForecasts['7am'].forEach((forecast, index) => {
        const button = document.createElement('button');
        const date = new Date(forecast.time);
        const buttonText = dayjs(date).format('dddd');
        button.textContent = buttonText;
        // button.dataset.day = forecast.day;
        button.className = 'btn-day-selector';

        // console.log(forecast);

        button.addEventListener('click', () => {
          updateMapLayer(forecast, rasterBeforeLayerId);

          controlsContainer.querySelectorAll('button').forEach(btn => btn.classList.remove('active'));
          button.classList.add('active');
        });

        controlsContainer.appendChild(button);

        if (index === 0) {
          button.classList.add('active');
        }
      });
    }

    const initialForecast = forecastData.availableForecasts['7am'][0];
    if (initialForecast) {
      updateMapLayer(initialForecast, rasterBeforeLayerId);
    }

    window.pymChild && window.pymChild.sendHeight();
  });

  // Suppress 404 tile errors that don't affect functionality
  map.on('error', (e) => {
    if (e.error && e.error.message && e.error.message.includes('404')) {
      // Check if this is a tileset not found error (vs individual tile 404s)
      if (e.error.message.includes('.json')) {
        // This is a tileset metadata 404 - tileset doesn't exist
        showErrorMessage();
      }
      // Suppress all 404 errors from appearing in console
      return;
    }
    console.error('Map error:', e);
  });
};

export default function (props, container, isMobile, mapParams) {
  const {
    initialWidth,
    initialHeight,
  } = props;

  const mapWrapper = container.select('#map-content-wrapper');
  if (mapWrapper.empty()) {
    console.error('#map-content-wrapper not found within #graphic. Cannot render map.');
    return;
  }

  mapWrapper.html('');
  const mapDiv = mapWrapper.append('div')
    .attr('id', 'map')
    .style('width', `${initialWidth}px`)
    .style('height', `${initialHeight}px`);

  drawMap(mapDiv, isMobile, mapParams);
}
